/* Shared visual layer only. Authentication, navigation, purchases and game logic remain owned by ROJO/Shopify. */
(() => {
  'use strict';
  const instances = new Map();
  let grain;
  function noise() {
    if (grain) return grain;
    try {
      const c = document.createElement('canvas'); c.width = c.height = 180;
      const ctx = c.getContext('2d'); if (!ctx) return '';
      const pixels = ctx.createImageData(180, 180); let seed = 27;
      for (let i = 0; i < pixels.data.length; i += 4) {
        seed = (seed * 1664525 + 1013904223) >>> 0;
        const v = seed >>> 24;
        pixels.data[i] = v; pixels.data[i + 1] = v * .7; pixels.data[i + 2] = v * .6; pixels.data[i + 3] = 90;
      }
      ctx.putImageData(pixels, 0, 0); grain = `url("${c.toDataURL()}")`; return grain;
    } catch (_) { return ''; }
  }
  function init(root) {
    if (!root || !root.hasAttribute('data-rojo-halloween') || instances.has(root)) return;
    const q = s => root.querySelector(s);
    const gate = root.hasAttribute('data-hw-gate');
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const timers = new Set(), off = [];
    let dirty = false;
    let disposed = false, visible = true, mode = '', cycle = 0, endBurst = 0, ghost, ctx;
    const filename = q('[data-hw-filename]'), originalName = filename?.textContent;
    const source = q('[data-rojo-canvas]');
    const later = (fn, ms) => {
      const id = setTimeout(() => { timers.delete(id); if (!disposed) { if (root.isConnected) fn(); else dispose(); } }, ms);
      timers.add(id); return id;
    };
    const listen = (el, event, fn) => { el.addEventListener(event, fn); off.push(() => el.removeEventListener(event, fn)); };
    function stop() {
      root.classList.remove('hw-burst', 'hw-omen', 'hw-feedback'); endBurst = 0;
      if (filename) filename.textContent = originalName;
      if (ctx && ghost) ctx.clearRect(0, 0, ghost.width, ghost.height);
    }
    function readMode() {
      if (gate) return 'password';
      if (q('[data-rojo-boot]:not(.is-complete)')) return 'boot';
      if (q('[data-rojo-cart-drawer].is-open')) return 'cart';
      if (!root.classList.contains('is-home')) return 'entry';
      if (!root.classList.contains('is-app')) return 'home';
      const app = q('[data-rojo-app].is-active') || q('[data-rojo-app][aria-hidden="false"]');
      const name = app?.getAttribute('data-rojo-app') || 'home';
      if (name === 'shop') return q('[data-rojo-product-viewport].is-focus-open, .rojo-product-card.is-flipped') ? 'product' : 'shop';
      if (name === 'rojo-run') return q('[data-rojo-run-shell]')?.dataset.rojoRunState === 'playing' ? 'playing' : 'run';
      if (name === 'concept' && q('[data-rojo-lightbox]:not([hidden]), [data-rojo-video-viewer]:not([hidden])')) return 'media';
      return name;
    }
    function busy() {
      const active = document.activeElement;
      return !!(root.contains(active) && active?.matches('input,textarea,select,[contenteditable="true"],.rp-show,.rp-unlock')) || !!q('[aria-busy="true"]');
    }
    const strong = () => ['password','entry','home','concept','run'].includes(mode);
    function pulse(feedback = false) {
      if (disposed || !visible || document.hidden || motion.matches || busy() || mode === 'boot' || mode === 'playing' || mode === 'media') return;
      stop();
      root.classList.add(feedback ? 'hw-feedback' : 'hw-burst');
      if (strong() && !feedback) {
        root.classList.add('hw-omen'); endBurst = performance.now() + 680;
        if (filename) filename.textContent = 'ROJO_HA▓LOWEEN.exe';
        later(() => { if (filename) filename.textContent = originalName; }, 330);
      }
      later(() => { root.classList.remove('hw-burst','hw-feedback'); endBurst = 0; }, feedback ? 360 : 680);
      later(() => root.classList.remove('hw-omen'), 1900);
    }
    let wasTransition = false;
    function sync() {
      if (disposed) return;
      const next = readMode(), transition = root.classList.contains('is-app-transition');
      if (next !== mode) {
        stop(); mode = next; root.dataset.hwMode = mode;
        if (!['boot','playing','media'].includes(mode)) later(() => pulse(mode === 'cart'), 100);
      } else if (transition && !wasTransition) pulse();
      wasTransition = transition;
    }
    function loop() {
      if (!root.isConnected) { dispose(); return; }
      if (strong()) pulse();
      later(loop, [5300,7200,6100,8300][cycle++ % 4]);
    }
    function positionVoice() {
      if (!source) return;
      const a = source.getBoundingClientRect(), b = root.getBoundingClientRect();
      root.style.setProperty('--hw-voice-top', `${Math.max(90, a.top - b.top + a.height * .33)}px`);
    }
    const n = noise(); if (n) root.style.setProperty('--hw-noise', n);
    if (source) {
      ghost = document.createElement('canvas'); ghost.className = 'hw-globe-damage';
      ghost.setAttribute('aria-hidden','true'); source.insertAdjacentElement('afterend',ghost);
      ctx = ghost.getContext('2d');
      root._hwPaintGlitch = now => {
        if (!ctx || now > endBurst || !['entry','home'].includes(mode) || motion.matches || busy()) {
          if (dirty && ctx && ghost.width) { ctx.clearRect(0,0,ghost.width,ghost.height); dirty=false; } return;
        }
        const w = Math.min(900, source.width), h = Math.round(source.height * w / Math.max(1,source.width));
        if (!w || !h) return;
        if (ghost.width !== w || ghost.height !== h) { ghost.width=w; ghost.height=h; }
        ctx.clearRect(0,0,w,h); dirty=true;
        try {
          const frame = Math.floor(now / 100);
          for (let k=0;k<5;k++) {
            const y = ((k * .19 + (frame % 3) * .035) % .95) * h, strip = h * (.027 + k * .006);
            const shift = ((frame+k)%2 ? 1:-1) * w * (.035 + k*.007);
            ctx.drawImage(source,0,y*source.height/h,source.width,strip*source.height/h,shift,y,w,strip);
          }
        } catch (_) { /* A unavailable WebGL copy cannot affect the live globe. */ }
      };
    }
    const observer = new MutationObserver(records => {
      sync();
      if (records.some(r => r.attributeName === 'hidden' && !r.target.hidden && /success|error/.test(r.target.className || ''))) pulse(true);
    });
    observer.observe(root,{attributes:true,attributeFilter:['class']});
    root.querySelectorAll('[data-rojo-boot],[data-rojo-app],[data-rojo-cart-drawer],[data-rojo-product-viewport],[data-rojo-run-shell],[data-rojo-lightbox],[data-rojo-video-viewer],[class*="success"],[class*="error"]').forEach(el => {
      observer.observe(el,{attributes:true,attributeFilter:['class','aria-hidden','hidden','data-rojo-run-state']});
    });
    let intersection, resize;
    if ('IntersectionObserver' in window) {
      intersection = new IntersectionObserver(entries => { visible=entries[0].isIntersecting; if (!visible) stop(); }); intersection.observe(root);
    }
    if ('ResizeObserver' in window && source) { resize=new ResizeObserver(positionVoice); resize.observe(source); }
    listen(root,'focusin',() => { if (busy()) stop(); });
    listen(document,'visibilitychange',() => { if (document.hidden) stop(); });
    listen(motion,'change',() => { stop(); root.classList.toggle('hw-reduced',motion.matches); });
    function dispose() {
      if (disposed) return; disposed=true; stop();
      timers.forEach(clearTimeout); timers.clear(); off.forEach(fn=>fn());
      observer.disconnect(); intersection?.disconnect(); resize?.disconnect();
      ghost?.remove(); delete root._hwPaintGlitch; delete root._hwFeedback; instances.delete(root);
    }
    root._hwFeedback = () => pulse(true);
    instances.set(root,dispose); root.classList.toggle('hw-reduced',motion.matches);
    sync(); positionVoice(); later(loop,850);
    if (q('[data-hw-password-error]')) later(() => pulse(true),200);
  }
  window.__rojoHalloweenWorld = init;
  const gates = () => document.querySelectorAll('[data-hw-gate]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',gates,{once:true}); else gates();
  document.addEventListener('shopify:section:load',gates);
  document.addEventListener('shopify:section:unload',event => {
    for (const [root,dispose] of instances) if (event.target.contains(root)) dispose();
  });
})();
